import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Heart, MessageCircle, Calendar } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { it } from 'date-fns/locale';

interface PostPreviewProps {
  postId: string;
}

interface PostData {
  id: string;
  content: string | null;
  image_url: string | null;
  likes_count: number;
  comments_count: number;
  created_at: string;
  location: string | null;
  user_id: string;
  profiles: {
    username: string;
    display_name: string | null;
    avatar_url: string | null;
  } | null;
  pets: {
    name: string;
    avatar_url: string | null;
  } | null;
}

const PostPreview: React.FC<PostPreviewProps> = ({ postId }) => {
  const [post, setPost] = useState<PostData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPostPreview();
  }, [postId]);

  const fetchPostPreview = async () => {
    try {
      // Fetch post with pet info
      const { data, error } = await supabase
        .from('posts')
        .select(`
          *,
          pets (name, avatar_url)
        `)
        .eq('id', postId)
        .maybeSingle();

      if (error) throw error;
      if (!data) return;

      // Fetch author profile separately
      const { data: profileData } = await supabase
        .from('profiles')
        .select('username, display_name, avatar_url')
        .eq('user_id', data.user_id)
        .maybeSingle();

      const merged = {
        ...data,
        profiles: profileData || null,
      } as PostData;

      setPost(merged);
    } catch (error) {
      console.error('Error fetching post preview:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="glass border-white/20 w-full animate-pulse">
        <CardContent className="p-3">
          <div className="flex items-center space-x-2 mb-2">
            <div className="w-6 h-6 bg-muted rounded-full" />
            <div className="h-3 bg-muted rounded w-20" />
          </div>
          <div className="h-3 bg-muted rounded w-full mb-2" />
          <div className="h-24 bg-muted rounded" />
        </CardContent>
      </Card>
    );
  }

  if (!post) {
    return (
      <Card className="glass border-white/20 w-full">
        <CardContent className="p-3 text-center">
          <p className="text-muted-foreground text-sm">Post non trovato</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Link to={`/app/post/${post.id}`} className="block w-full">
      <Card className="glass border-white/20 hover:border-primary/20 transition-all cursor-pointer w-full">
        <CardContent className="p-3">
          {/* Author info */}
          <div className="flex items-center space-x-2 mb-2">
            <Avatar className="w-6 h-6">
              <AvatarImage src={post.profiles?.avatar_url || undefined} />
              <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                {post.profiles?.username.charAt(0).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-white truncate">
                {post.profiles?.display_name || post.profiles?.username}
              </p>
              <p className="text-xs text-muted-foreground">
                {formatDistanceToNow(new Date(post.created_at), { 
                  addSuffix: true, 
                  locale: it 
                })}
                {post.pets && (
                  <> • con {post.pets.name}</>
                )}
              </p>
            </div>
          </div>

          {/* Content */}
          {post.content && (
            <p className="text-sm text-white mb-2 line-clamp-2">
              {post.content}
            </p>
          )}

          {/* Image */}
          {post.image_url && (
            <img 
              src={post.image_url} 
              alt="Post preview" 
              className="w-full h-32 object-cover rounded mb-2"
            />
          )}

          {/* Location */}
          {post.location && (
            <p className="text-xs text-muted-foreground mb-2 flex items-center">
              <Calendar className="w-3 h-3 mr-1" />
              {post.location}
            </p>
          )}

          {/* Stats */}
          <div className="flex items-center space-x-4 text-xs text-muted-foreground">
            <div className="flex items-center space-x-1">
              <Heart className="w-3 h-3" />
              <span>{post.likes_count}</span>
            </div>
            <div className="flex items-center space-x-1">
              <MessageCircle className="w-3 h-3" />
              <span>{post.comments_count}</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};

export default PostPreview;